import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Btn } from "./UI";
import { Globe, Loader2, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";

const BRAND_DOMAIN = "mg.totalreach.us";

export const EmailDomainSetup = ({ domainStatus, onSwitched }) => {
  const [setup, setSetup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const runSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/war-room/email-domain/setup", { domain: BRAND_DOMAIN });
      setSetup(data);
      toast.success(`${BRAND_DOMAIN} is registered on Mailgun — add the DNS records below at your domain registrar`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Domain setup failed");
    } finally { setBusy(false); }
  };

  const runVerify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/war-room/email-domain/verify", { domain: BRAND_DOMAIN });
      if (data.switched) {
        toast.success(`${BRAND_DOMAIN} verified — all emails now send from your brand domain`);
        onSwitched && onSwitched();
        setSetup(null);
      } else {
        toast.warning(`Still ${data.state} — DNS changes can take up to 24h to propagate. Try again later.`);
        runSetup();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setVerifying(false); }
  };

  const brandActive = domainStatus?.brand?.state === "active";
  const usingBrand = domainStatus?.current?.domain === BRAND_DOMAIN;

  return (
    <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="email-domain-setup">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-slate-400 flex items-center gap-2">
          <Globe className="w-3.5 h-3.5 text-cyan-300" />
          Brand sending domain <span className="font-mono text-slate-200">{BRAND_DOMAIN}</span>:
          <span className={brandActive ? "text-emerald-300" : "text-amber-300"}>{domainStatus?.brand?.state || "…"}</span>
          {usingBrand && <span className="text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> in use</span>}
        </div>
        <div className="flex gap-2">
          {!brandActive && !setup && (
            <Btn variant="secondary" onClick={runSetup} disabled={busy} data-testid="domain-setup-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Show DNS records
            </Btn>
          )}
          {!usingBrand && (
            <Btn variant="secondary" onClick={runVerify} disabled={verifying} data-testid="domain-verify-btn">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Verify & switch
            </Btn>
          )}
        </div>
      </div>
      {setup && (
        <div className="mt-3">
          <div className="text-[11px] text-slate-500 mb-2">Add these records at your DNS provider for <b>totalreach.us</b>, then click "Verify & switch":</div>
          <div className="space-y-1.5" data-testid="dns-records-list">
            {(setup.dns_records || []).map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] font-mono bg-slate-900/70 rounded-md px-3 py-2 overflow-x-auto">
                <span className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] ${r.valid === "valid" ? "bg-emerald-400/15 text-emerald-300" : "bg-amber-400/15 text-amber-300"}`}>{r.valid === "valid" ? "OK" : "ADD"}</span>
                <span className="text-cyan-300 shrink-0">{r.type}</span>
                <span className="text-slate-300 shrink-0">{r.name}</span>
                <span className="text-slate-500 truncate flex-1">{r.value}</span>
                <button onClick={() => { navigator.clipboard.writeText(r.value); toast.success("Value copied"); }} className="text-slate-500 hover:text-cyan-300 shrink-0"><Copy className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
